import { X } from 'lucide-react';
import { SelectInput } from './SelectInput';
import { TextInput } from './TextInput';

/**
 * Wire shape for a custom field tacked onto an entity (Organisation, Party,
 * etc.). Stored on the BE as a JSON-stringified `ExtraField[]` inside the
 * entity's `extraFields` text column — the FE owns the schema entirely.
 *
 * `fieldLabel` + `fieldType` are immutable once a field is created; only
 * `fieldValue` is editable through this component. Deleting and re-adding
 * is the supported way to "rename" or change type.
 */
export interface ExtraField {
  fieldLabel: string;
  fieldValue: string;
  fieldType: ExtraFieldType;
}

export type ExtraFieldType = 'text' | 'number' | 'date' | 'boolean';

export interface ExtraFieldEditorProps {
  field: ExtraField;
  onValueChange: (value: string) => void;
  onDelete: () => void;
  /** When true, the value input renders read-only and the delete button hides. */
  disabled?: boolean;
}

/**
 * Single-row editor for one custom field. Renders the input variant matching
 * `fieldType` (text input, numeric text input, native date picker, or a
 * Yes/No select) plus a small × button to remove the field from its parent
 * collection. Lifted from My.Tms.Old/components/ExtraFieldInlineEditor.tsx
 * with the inputs migrated to the new `@my/ui` primitives.
 */
export function ExtraFieldEditor({
  field,
  onValueChange,
  onDelete,
  disabled = false,
}: ExtraFieldEditorProps) {
  const inputName = `extra-${field.fieldLabel.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <div className="flex items-start gap-1">
      <div className="flex-1">
        {field.fieldType === 'boolean' ? (
          <SelectInput
            label={field.fieldLabel}
            name={inputName}
            value={field.fieldValue}
            onChange={(e) => onValueChange(e.target.value)}
            disabled={disabled}
            options={[
              { value: 'true', label: 'Yes' },
              { value: 'false', label: 'No' },
            ]}
            placeholder="Select…"
          />
        ) : (
          <TextInput
            label={field.fieldLabel}
            name={inputName}
            type={field.fieldType === 'date' ? 'date' : 'text'}
            inputMode={field.fieldType === 'number' ? 'numeric' : undefined}
            value={field.fieldValue}
            disabled={disabled}
            onChange={(e) =>
              onValueChange(
                // Strip non-digits for `number` so the saved JSON is always a
                // clean numeric string. Leading minus / decimal can be added
                // back when a real numeric input lands in `@my/ui`.
                field.fieldType === 'number'
                  ? e.target.value.replace(/[^\d.-]/g, '')
                  : e.target.value,
              )
            }
            autoComplete="off"
          />
        )}
      </div>
      {/*
        The remove button is intentionally only available while the field is
        empty — once the user has typed a value (or picked Yes/No), they have
        to clear it first. Removes the "I clicked × by accident and lost a
        date / number I'd already typed" foot-gun without sacrificing the
        affordance for genuinely-unwanted fields.
      */}
      {!disabled && !field.fieldValue && (
        <button
          type="button"
          aria-label={`Remove ${field.fieldLabel}`}
          onClick={onDelete}
          className="mt-1 flex h-7 w-7 items-center justify-center rounded-sm text-slate-500 hover:bg-red-50 hover:text-red-600"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
